import { createSlice } from "@reduxjs/toolkit";
import { StoreDispatch } from "../store";
import { getReportContent, ReportsContentRequest } from "src/network/graphql/analyticsService";
import { ReportsContent } from "../reportsContent/reportsContentSlice";
import moment from "moment";
import { searchMCC } from "src/network/graphql/transactionService";
import { formatToRunningCase } from "src/utils";

interface AnalyticsState {
  loading: boolean;
  error: any;
  analytics: {
    data: ReportsContent[];
    barChart: {
      weeks: string[];
      totalTransactions: number[];
      totalDeposits: number[];
    };
    pieChart: {
      categories: string[];
      amounts: number[];
    };
    lineChart: {
      weeks: string[];
      amount: number[];
    };
  };
}

const initialAnalytics: ReportsContent[] = [];

const initialState: AnalyticsState = {
  loading: false,
  error: null,
  analytics: {
    data: initialAnalytics,
    barChart: {
      weeks: [],
      totalTransactions: [],
      totalDeposits: []
    },
    pieChart: {
      categories: [],
      amounts: []
    },
    lineChart: {
      weeks: [],
      amount: []
    }
  }
};

const analyticsSlice = createSlice({
  name: "analytics",
  initialState,
  reducers: {
    fetchAnalyticsStart: (state) => {
      state.loading = true;
    },
    fetchAnalyticsSuccess: (state, action) => {
      state.loading = false;
      state.analytics = action.payload;
    },
    fetchAnalyticsFail: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    }
  }
});

export const { fetchAnalyticsStart, fetchAnalyticsSuccess, fetchAnalyticsFail } = analyticsSlice.actions;

const processReportData = (reportData: any[], today: moment.Moment, startDate: moment.Moment, days: number) => {
  const numberOfBuckets = 4;
  const bucketSize = days / numberOfBuckets;

  const transactions = Array(numberOfBuckets).fill(0);
  const deposits = Array(numberOfBuckets).fill(0);
  const numberOfTransactions = Array(numberOfBuckets).fill(0);
  const mccMap = new Map<string, number>();

  reportData.forEach((report: any) => {
    const reportDate = moment(report.report_date);

    if (reportDate.isBetween(startDate, today, undefined, "[]")) {
      const parsedData = JSON.parse(report.data);
      const {
        today_transactions_amount = 0,
        today_deposit_amount = 0,
        today_transactions = 0,
        total_transactions_per_mcc = {}
      } = parsedData;

      const daysDiff = today.diff(reportDate, "days");
      const bucketIndex = numberOfBuckets - 1 - Math.floor(daysDiff / bucketSize);

      if (bucketIndex >= 0 && bucketIndex < numberOfBuckets) {
        transactions[bucketIndex] += today_transactions_amount;
        deposits[bucketIndex] += today_deposit_amount;
        numberOfTransactions[bucketIndex] += today_transactions;
      }

      Object.values(total_transactions_per_mcc).forEach((mcc: any) => {
        const amountSpent = mcc.amount_spent || 0;

        mccMap.set(mcc.mcc, (mccMap.get(mcc.mcc) || 0) + amountSpent);
      });
    }
  });

  return { transactions, deposits, numberOfTransactions, mccMap };
};

const fetchMCCData = async (mccMap: Map<string, number>) => {
  const mccArray = Array.from(mccMap.keys());
  const mccResponse = await searchMCC({
    filter: { mcc_filter: { mcc: mccArray } }
  });

  return mccResponse.data.search_mcc.items;
};

const buildPieChartData = (mccMap: Map<string, number>, mccItems: any[]) => {
  const pieCategories: string[] = [];
  const pieAmounts: number[] = [];

  // mccMap.forEach((amount, mccId) => {
  //   const mccItem = mccItems.find((item: any) => item.mcc === mccId);
  //   const categoryName = mccItem ? mccItem.mcc_category : "Unknown MCC";

  //   pieCategories.push(categoryName);
  //   pieAmounts.push(amount);
  // });

  const categoryMap = new Map<string, number>(); // New map to store amounts by category

  mccMap.forEach((amount, mccId) => {
    const mccItem = mccItems.find((item: any) => item.mcc === mccId);
    const categoryName = mccItem ? mccItem.mcc_category : "Unknown";

    // Accumulate amounts by category
    categoryMap.set(categoryName, (categoryMap.get(categoryName) || 0) + amount);
  });

  // Convert categoryMap to arrays for pie chart
  categoryMap.forEach((amount, category) => {
    pieCategories.push(formatToRunningCase(category));
    pieAmounts.push(amount);
  });

  return { pieCategories, pieAmounts };
};

export const fetchAnalyticsAction = (data: ReportsContentRequest, days: number) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchAnalyticsStart());
    try {
      const response = await getReportContent(data);
      const reportData = response.data.search_report_content.report_content_list;
      const structuredResponse = reportData.map((data: any) => ({
        object_id: data.object_id,
        report_date: data.report_date,
        object_type: data.object_type,
        data: JSON.parse(data.data)
      }));

      const today = moment().subtract(1, "days").endOf("day");
      const startDate = moment()
        .subtract(days + 1, "days")
        .startOf("day");

      const { transactions, deposits, numberOfTransactions, mccMap } = processReportData(
        reportData,
        today,
        startDate,
        days
      );
      const mccItems = await fetchMCCData(mccMap);
      const { pieCategories, pieAmounts } = buildPieChartData(mccMap, mccItems);

      const lineWeekLabels = Array.from({ length: 4 }, (_, index) => {
        const weekStartDate = today.clone().subtract(index * (days / 4), "days");

        return weekStartDate.format("DD/MM/YYYY");
      }).reverse();

      const barWeekLabels = Array.from({ length: 4 }, (_, index) => {
        const weekEndDate = today.clone().subtract(index * (days / 4), "days");
        // .startOf("isoWeek");
        const weekStartDate = weekEndDate.clone().subtract(days / 4 - 1, "days");

        return `${weekStartDate.format("DD/MM/YYYY")} - ${weekEndDate.format("DD/MM/YYYY")}`;
      }).reverse();

      dispatch(
        fetchAnalyticsSuccess({
          data: structuredResponse,
          barChart: {
            // weeks: transactions.map((_, index) => `Week ${index + 1}`),
            weeks: barWeekLabels,
            totalTransactions: transactions,
            totalDeposits: deposits
          },
          pieChart: {
            categories: pieCategories,
            amounts: pieAmounts
          },
          lineChart: {
            // weeks: numberOfTransactions.map((_, index) => `Week ${index + 1}`),
            weeks: lineWeekLabels,
            amount: numberOfTransactions
          }
        })
      );
    } catch (error) {
      dispatch(fetchAnalyticsFail(error));
    }
  };
};

export default analyticsSlice.reducer;
